import React, { useEffect, useState } from "react";
import { Chip, Fade } from "@mui/material";
import { BookmarkPlus } from "lucide-react";

export interface FactsUpdatedPillProps {
  /** Monotonic counter from TurnState.factsUpdatedAt. Each increment
   *  re-shows the pill and starts a fresh fade timer; back-to-back
   *  updates collapse into one continuous display. */
  triggerKey: number;
  /** ms before the pill auto-fades. */
  durationMs?: number;
}

/**
 * Transient "Memory updated" pill — mirrors ChatGPT's inline memory
 * indicator. Renders nothing until the first facts_updated frame, then
 * fades back out so it doesn't crowd the message list.
 */
export function FactsUpdatedPill({
  triggerKey,
  durationMs = 4000,
}: FactsUpdatedPillProps) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (triggerKey <= 0) return;
    setVisible(true);
    const t = setTimeout(() => setVisible(false), durationMs);
    return () => clearTimeout(t);
  }, [triggerKey, durationMs]);

  if (triggerKey <= 0) return null;

  return (
    <Fade in={visible} unmountOnExit timeout={{ enter: 200, exit: 400 }}>
      <Chip
        size="small"
        icon={<BookmarkPlus size={12} />}
        label="Memory updated"
        sx={{
          my: 0.5,
          bgcolor: "primary.main",
          color: "primary.contrastText",
          "& .MuiChip-icon": { color: "primary.contrastText" },
        }}
        aria-live="polite"
      />
    </Fade>
  );
}
